import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const femaleHormoneBaseline: ContentBlock<"module"> = {
  id: makeId("module-female-hormone-baseline"),
  kind: "module",
  tags: ["her", "assessment", "testing"],
  title: "Fertility Baseline (Her)",
  subtitle: "Full hormone panel and imaging to understand your starting point",
  icon: "🔬",
  body: {
    duration: "1–2 weeks",
    category: "assessment",
    forWhom: "her",
    includes: [
      "Hormone panel (AMH, FSH, LH, estradiol) drawn on cycle day 2–3",
      "Thyroid panel (TSH, free T4)",
      "Vitamin D level",
      "Transvaginal ultrasound with antral follicle count (AFC)",
      "Results review with a fertility nurse",
    ],
    trackableMarkers: [
      {
        name: "AMH",
        expectedToChange: false,
        expectation:
          "AMH reflects your follicle pool and generally only declines with age. This establishes your baseline for planning.",
        retestInterval: "6 months",
      },
      {
        name: "FSH",
        expectedToChange: false,
        expectation:
          "Day-3 FSH indicates ovarian function. Trending upward is a signal to act sooner rather than later.",
        retestInterval: "3–6 months",
      },
      {
        name: "TSH",
        expectedToChange: true,
        expectation:
          "If elevated, medication can bring TSH to the fertility goal of <2.5. Typically corrected within 6–8 weeks.",
        retestInterval: "6–8 weeks after starting medication",
      },
      {
        name: "Vitamin D",
        expectedToChange: true,
        expectation:
          "Deficiency is common and correctable with supplementation. Goal is >40 ng/mL.",
        retestInterval: "8 weeks",
      },
      {
        name: "AFC",
        expectedToChange: false,
        expectation:
          "Antral follicle count varies cycle to cycle by 2–5 follicles. Useful for baseline, not as an optimization metric.",
      },
    ],
    priceRange: { low: 150, high: 400 },
    currency: "USD",
    status: "coming-soon",
    steps: [
      {
        title: "Schedule labs",
        description:
          "Blood draw must be on cycle day 2–3 for accurate FSH, LH, and estradiol. We help you book at a convenient location.",
      },
      {
        title: "Ultrasound",
        description:
          "Transvaginal ultrasound to count antral follicles and check for structural issues (fibroids, polyps, cysts).",
      },
      {
        title: "Results review",
        description:
          "30-minute call with a fertility nurse to walk through your numbers and discuss next steps.",
      },
    ],
  },
  sources: [
    {
      title:
        "Diagnostic evaluation of the infertile female: a committee opinion",
      journal: "Fertility and Sterility",
      year: 2021,
      evidenceLevel: "expert-opinion",
    },
  ],
};

registerBlock(femaleHormoneBaseline);
